import moment from 'moment'
import { v4 as uuidv4 } from 'uuid'
import { FILE_FORMATS } from '@/helpers/constants'
import store from '@/store/store'

/**
 * Ожидает операцию для выполнения и возвращает ошибку при таймауте ее выполнения
 * @param {Promise<any>} asyncPromise Операция для ожидания
 * @param {number} timeLimit Лимит времени в секундах (дефолт - 60сек)
 * @param {string} callName Название функции для ошибки
 * @returns {Promise<any> | undefined} Результат выполнения, либо ошибка для блока try...catch
 */
export const asyncCallWithTimeout = async (asyncPromise, timeLimit = 60, callName = 'unnamed') => {
  let timeoutHandle

  const timeoutPromise = new Promise((resolve, reject) => {
    timeoutHandle = setTimeout(
      () => reject({
        timeout: true,
        text: new Error(`Превышено время ожидания выполнения функции "${callName}"`)
      }),
      Number(timeLimit) * 1000
    )
  })

  return Promise.race([asyncPromise, timeoutPromise]).then(result => {
    clearTimeout(timeoutHandle)
    return result
  })
}

/**
 * Возвращает расширение файла
 * @param {File} file
 */
export const fileExtension = file => {
  return file.name.substring(file.name.lastIndexOf('.'), file.name.length) || file.name
}

/**
 * Проверяет формат файлов на тип 'сертификат' и выводит сообщение об ошибке если найден хотя бы один не подходящий
 * @param {FileList} files
 */
export const checkFilesForCertificate = (files) => {
  try {
    const availableExtensions = FILE_FORMATS.CERTIFICATES.split(', ')
    const notCertNames = []

    for (const file of files) {
      const thisExtension = fileExtension(file)
      if (!availableExtensions.includes(thisExtension)) {
        notCertNames.push(file.name)
      }
    }
    if (notCertNames.length) {
      const fileText = notCertNames.length === 1 ? 'Файл' : 'Файлы'
      const certText = notCertNames.length === 1 ? 'сертификат' : 'сертификаты'
      const fileNames = notCertNames.join(', ')

      store.dispatch(
        'notification/showAlert',
        `${fileText} ${fileNames} не удалось распознать как ${certText}. Могут возникнуть ошибки при проверке`
      )
    }
  } catch (e) {
    console.error('Что-то пошло не так с проверкой формата файла', e)
  }
}

/**
 * Проверяет на массив и наличие в нем элементов
 * @param {any} array
 * @returns {Boolean}
 */
export function isArray (array) {
  return array && Array.isArray(array) && !!array.length
}

/**
 * Проверяет не пустой ли объект
 * @param {Object} obj
 * @returns {Boolean}
 */
export function isNotEmptyObj (obj) {
  return obj &&
    typeof obj === 'object' &&
    Object.keys(obj).length !== 0 &&
    Object.getPrototypeOf(obj) === Object.prototype
}

/**
 * Конвертирует файл в FormData объект для отправки
 * @param {File} file
 */
export function formDataFile (file) {
  if (!file) {
    return null
  }

  const formData = new FormData()
  formData.append('document', file)
  return formData
}

/**
 * Скачивает Blob файл
 * @param {Blob} blobFile
 * @param {String} filename
 */
export function downloadBlob (blobFile, filename) {
  if (!blobFile) {
    return
  }

  if (window.navigator?.msSaveOrOpenBlob) {
    window.navigator.msSaveOrOpenBlob(blobFile, filename)
    return
  }

  const link = document.createElement('a')
  const objectUrl = URL.createObjectURL(blobFile)
  link.href = objectUrl
  link.download = filename ?? 'unknown'
  document.body.appendChild(link)
  link.click()

  setTimeout(() => {
    URL.revokeObjectURL(objectUrl)
    document.body.removeChild(link)
  }, 100)
}

/**
 * Конвертирует файл в base64 для отправки
 * @param {File} file
 */
export function fileToBase64 (file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(
      reader.result.length
        ? reader.result.split(',')[1]
        : reader.result
    )
    reader.onerror = error => reject(error)
  })
}

/**
 * Возвращает true если браузер - Internet Explorer
 * @returns {Boolean}
 */
export function browserIsIE () {
  return !!window.document.documentMode
}

/**
 * Локализует дату в формате 2021-12-22T06:39:23 в 22 декабря 2021 г., 9:39
 * @param {String} date
 * @returns {String}
 */
export function localizeDate (date) {
  return moment(`${date}+00:00`).local().locale('ru').format('LLL')
}

/**
 * Локализует дату в формате Unix-Time (кол-во секунд от 1970г.) в формат DD.MM.YYYY HH:mm
 * @param {String | Number} seconds
 * @returns {String}
 */
export function localizeUnixDate (seconds) {
  const unixDate = typeof seconds !== 'number'
    ? Number(seconds)
    : seconds
  return moment.unix(unixDate).local().format('DD.MM.YYYY HH:mm')
}

/**
 * Формирует данные для дополнительных параметров при запросе на проверку подписи
 * @param {Object} params
 * @constructor
 */
export function VerifyPackageRequest (params) {
  this.SignatureType = params.format ?? ''

  if (params.format === 'CMS' || params.format === 'GOST3410') {
    this.VerifyParams = {
      Hash: params.sigType === 'hashSign' ? 'true' : 'false'
    }

    if (
      (params.format === 'CMS' && params.sigType === 'hashSign') ||
      params.format === 'GOST3410'
    ) {
      this.VerifyParams.HashAlgorithm = params.algorithm
    }
  }

  if (params.certificate) {
    this.Certificate = params.certificate
  }

  if (isArray(params.plugins)) {
    const activatedPlugins = params.plugins.filter(plugin => plugin.value)
    this.CertVerifiersPluginsIds = activatedPlugins.length ? activatedPlugins.map(plugin => plugin.id) : []
  } else {
    this.CertVerifiersPluginsIds = []
  }
}

/**
 * Формирует данные при запросе на проверку сертификата
 * @param {String} base64File
 * @param {Object[]} options
 * @constructor
 */
export function CertificateRequest (base64File, options) {
  this.Content = base64File

  if (isArray(options)) {
    const activatedPlugins = options.filter(plugin => plugin.value)
    this.CertVerifiersPluginsIds = activatedPlugins.length ? activatedPlugins.map(plugin => plugin.id) : []
  } else {
    this.CertVerifiersPluginsIds = []
  }
}

/**
 * Обертка ответа сервера при верификации
 * @param {Object|null} successResponse
 * @param {String|null} errorMsg
 * @return {Object}
 * @constructor
 */
export function VerificationResponse ({ successResponse = null, errorMsg = null }) {
  return {
    result: !!successResponse,
    data: successResponse?.data ?? null,
    errorMsg
  }
}

/**
 * Реконструирует данные результатов проверки подписи из ответа
 * @param {VerificationResponse} responseData
 * return {Object[]|null}
 * @constructor
 */
export function VerificationResultSignature (responseData) {
  if (!responseData.result) {
    console.error('Неожиданный формат данных при трансформации результатов проверки')
    return null
  }

  const resultArr = responseData.data.Results

  if (!Array.isArray(resultArr) || !resultArr.length) {
    console.error('При трансформации результатов не найден массив с данными')
    return null
  }

  return resultArr.map(allResults => {
    const signatureResults = allResults.VerificationResults.map(result => {
      const main = () => {
        if (!result) {
          return null
        }

        const mainArr = [
          {
            key: 'Результат проверки',
            value: result.Result ? 'Подпись действительна' : 'Подпись недействительна',
            status: result.Result
          }
          // { key: 'Документ', value: allResults.SignedDocumentName || 'Не указан' }
        ]

        if (allResults.OriginalDocumentName) {
          mainArr.push({ key: 'Исходный документ', value: allResults.OriginalDocumentName })
        }

        if (result.Message) {
          mainArr.push({
            key: 'Дополнительная информация',
            value: result.Message,
            status: result.Result ? null : false
          })
        }

        return mainArr
      }

      const signature = () => {
        const info = result?.SignatureInfo

        if (!info) {
          return null
        }

        const mainArr = []

        if (info.CAdESType) {
          mainArr.push({ key: 'Формат подписи CAdES', value: info.CAdESType })
        }

        if (info.SigningTimeUTC || info.SigningTime) {
          mainArr.push({
            key: 'Время подписи, полученное из штампа',
            value: info.SigningTimeUTC ? localizeUnixDate(info.SigningTimeUTC) : info.SigningTime
          })
        }

        if (info.LocalSigningTimeUTC || info.LocalSigningTime) {
          mainArr.push({
            key: 'Время подписи',
            value: info.LocalSigningTimeUTC ? localizeUnixDate(info.LocalSigningTimeUTC) : info.LocalSigningTime
          })
        }

        return mainArr.length ? mainArr : null
      }

      const certificate = () => {
        const info = result?.SignerCertificateInfo

        if (!info) {
          return null
        }

        return [
          { key: 'Субъект', value: info.SubjectName || 'Неизвестно' },
          { key: 'Издатель', value: info.IssuerName || 'Неизвестно' },
          { key: 'Серийный номер', value: info.SerialNumber || 'Неизвестно' },
          {
            key: 'Срок действия',
            value: `${info.NotBeforeUTC ? localizeUnixDate(info.NotBeforeUTC) : info.NotBefore} 
            - ${info.NotAfterUTC ? localizeUnixDate(info.NotAfterUTC) : info.NotAfter}`
          },
          { key: 'Отпечаток сертификата', value: info.Thumbprint || 'Неизвестно' }
        ]
      }

      const plugins = () => {
        if (!result?.AdditionalCertificateResult?.length) {
          return null
        }

        return result.AdditionalCertificateResult.map(resultArr => {
          return {
            title: resultArr.PluginDescription,
            id: resultArr.AssemblyName,
            data: [
              {
                key: 'Результат проверки',
                value: resultArr.bResult ? 'Сертификат прошел проверку' : 'Сертификат не прошел проверку',
                status: resultArr.bResult
              },
              { key: 'Ошибки', value: resultArr.ErrorsList }
            ]
          }
        })
      }

      return {
        id: uuidv4() || '0',
        status: result?.Result,
        info: {
          main: main(),
          signature: signature(),
          certificate: certificate(),
          plugins: plugins()
        }
      }
    })

    return {
      titleMain: allResults.SignedDocumentName || null,
      titleDoc: allResults.OriginalDocumentName || null,
      docIdToDownload: allResults.CanExtractContent,
      id: uuidv4() || '0',
      signatureResults
    }
  })
}

/**
 * Реконструирует данные результатов проверки сертификата из ответа
 * @param {VerificationResponse} responseData
 * @param {String} title
 * return {Object[]|null}
 * @constructor
 */
export function VerificationResultCertificate (responseData, title) {
  if (!responseData.result) {
    console.error('Неожиданный формат данных при трансформации результатов проверки')
    return null
  }

  const resultArr = responseData.data

  const main = () => {
    if (!resultArr) {
      return null
    }

    const mainArr = [
      {
        key: 'Результат проверки',
        value: resultArr.Result ? 'Сертификат прошел проверку' : 'Сертификат не прошел проверку',
        status: resultArr.Result
      },
      { key: 'Название документа', value: title || 'Не указан' }
    ]

    if (resultArr.Message) {
      mainArr.push({
        key: 'Дополнительная информация',
        value: resultArr.Message,
        status: resultArr.Result ? null : false
      })
    }

    return mainArr
  }

  const certificate = () => {
    const info = resultArr.SignerCertificateInfo

    if (!info) {
      return null
    }

    return [
      { key: 'Субъект', value: info.SubjectName || 'Неизвестно' },
      { key: 'Издатель', value: info.IssuerName || 'Неизвестно' },
      { key: 'Серийный номер', value: info.SerialNumber || 'Неизвестно' },
      {
        key: 'Срок действия',
        value: `${info.NotBeforeUTC ? localizeUnixDate(info.NotBeforeUTC) : info.NotBefore} 
        - ${info.NotAfterUTC ? localizeUnixDate(info.NotAfterUTC) : info.NotAfter}`
      },
      { key: 'Отпечаток сертификата', value: info.Thumbprint || 'Неизвестно' }
    ]
  }

  const plugins = () => {
    if (!resultArr?.AdditionalCertificateResult?.length) {
      return null
    }

    return resultArr.AdditionalCertificateResult.map(resultArr => {
      return {
        title: resultArr.PluginDescription,
        id: resultArr.AssemblyName,
        data: [
          {
            key: 'Результат проверки',
            value: resultArr.bResult ? 'Сертификат прошел проверку' : 'Сертификат не прошел проверку',
            status: resultArr.bResult
          },
          { key: 'Ошибки', value: resultArr.ErrorsList }
        ]
      }
    })
  }

  return {
    titleMain: title || 'Не указан',
    id: uuidv4() || '0',
    status: resultArr?.Result,
    info: {
      main: main(),
      certificate: certificate(),
      plugins: plugins()
    }
  }
}

/**
 * Маппер сообщения об ошибке
 * @param {Object|String} axiosError
 * @return {String}
 */
export function ErrorMessage (axiosError) {
  if (!axiosError) {
    return 'empty'
  }

  if (isNotEmptyObj(axiosError) && axiosError.Error) {
    return axiosError.Error
  }

  if (!axiosError.response?.data) {
    if (axiosError.Message) {
      return axiosError.Message
    }

    if (axiosError.title && axiosError.type) {
      return `${axiosError.title} Error from ${axiosError.type}`
    }

    if (axiosError.message && axiosError.code) {
      return `${axiosError.message}. Code: ${axiosError.code}`
    }

    return JSON.stringify(axiosError)
  }

  const data = axiosError.response.data

  if (isNotEmptyObj(data.errors)) {
    Object.values(data.errors).forEach(errorArr => {
      if (isArray(errorArr)) {
        errorArr.forEach(error => console.error(`Ошибка! ${error}`))
      } else {
        console.error(`Ошибка! ${errorArr}`)
      }
    })
  }

  if (!data.ErrorDescription && !data.Error) {
    return data.Message || data.title || 'error'
  }

  return `${data.ErrorDescription} (${data.Error})`
}
